import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowUpDown,
  ArrowUpRight,
  Ban,
  Bell,
  CalendarClock,
  CheckSquare,
  Copy,
  FileDown,
  FileStack,
  FileText,
  FileUp,
  Globe,
  Layers,
  LayoutGrid,
  ListChecks,
  type LucideIcon,
  Megaphone,
  ShieldAlert,
  Sparkles,
  StickyNote,
  Tags,
  Target,
  Trophy,
  Undo2,
  WandSparkles,
} from "lucide-react";

import type {
  AsyncTask,
  AsyncTaskTypeId,
} from "@/modern/components/async-tasks/types";
import {
  boardExportFallbackFileName,
  downloadBoardExportResult,
} from "@/modern/components/async-tasks/utils/boardExportDownload";
import { ALERT_TYPES, ASYNC_TASK_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { IMPORT_TYPES_HISTORY } from "@/other/constants/OtherKeys.jsx";
import { downloadFileFromData, showToast } from "@/other/utils/utils";
import type {
  MappedNotificationCounts,
  NavigateTarget,
  Notification,
  NotificationActionDescriptor,
  NotificationCategoryGroup,
  NotificationConfigContext,
  NotificationCountsResponse,
  NotificationFilter,
  NotificationFilterType,
  NotificationListParams,
  NotificationMappedCategoryCount,
  NotificationOpenTarget,
  NotificationTaskResult,
  NotificationTypeEntry,
} from "../types";
import {
  boardAlertDescription,
  emphasizePhrase,
  fileDescription,
  generalDescription,
  isStatusCategoryNotification,
  leadsDescription,
  noteDescription,
  nudgeDescription,
  pickTrimmed,
  reminderDescription,
  resolveBoardLabel,
  scheduleDescription,
  statusDescription,
} from "../utils/descriptions";

// ─── Private Helpers ───────────────────────────────────────────────────────────

type UnknownRecord = Record<string, unknown>;
type Awaitable<T> = T | Promise<T>;

function meta(notification: Notification): UnknownRecord {
  return (notification.async_task?.meta_data ?? {}) as UnknownRecord;
}

function stringMeta(notification: Notification, key: string): string | undefined {
  const value = meta(notification)[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function numberMeta(notification: Notification, key: string): number | undefined {
  const value = meta(notification)[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function recordLabel(record: unknown): string | undefined {
  if (!record || typeof record !== "object") return undefined;
  const value = record as { name?: string | null; title?: string | null; displayName?: string | null };
  return value.name?.trim() || value.title?.trim() || value.displayName?.trim() || undefined;
}

function resolveLabel(
  context: NotificationConfigContext,
  kind: Parameters<NonNullable<NotificationConfigContext["resolveEntityLabel"]>>[0],
  id: number | undefined,
): string | undefined {
  if (id == null) return undefined;
  const explicit = context.resolveEntityLabel?.(kind, id);
  if (explicit) return explicit;
  if (kind === "board") {
    return recordLabel(context.boardManager?.boardNameStore?.getById?.(id));
  }
  const storeKey = {
    "work-file": STORE_KEYS.marketingWorkfileStore,
    "marketing-page": STORE_KEYS.marketingPageStore,
    incentive: STORE_KEYS.outcomeIncentiveStore,
    context: STORE_KEYS.boardContextStore,
  }[kind];
  const fromCrud = recordLabel(context.project?.getCrudStore?.(storeKey)?.getById?.(id));
  if (fromCrud) return fromCrud;
  return kind === "incentive"
    ? recordLabel(context.project?.outcomeIncentiveStore?.getById?.(id))
    : undefined;
}

function taskStatus(notification: Notification) {
  return notification.async_task?.status ?? null;
}

function actorName(
  notification: Notification,
  context: NotificationConfigContext,
): string | null {
  const embedded = notification.async_task?.creator_name?.trim();
  if (embedded) return embedded;
  const creator = notification.async_task?.creator ?? notification.creator;
  if (creator == null) return null;
  return context.resolveUserName?.(creator) ?? null;
}

const DATA_TYPE_LABELS: Record<number, string> = {
  [IMPORT_TYPES_HISTORY.tasks]: "tasks",
  [IMPORT_TYPES_HISTORY.resources]: "resources",
  [IMPORT_TYPES_HISTORY.clients]: "clients",
  [IMPORT_TYPES_HISTORY.contacts]: "contacts",
  [IMPORT_TYPES_HISTORY["tasks-budget"]]: "task budgets",
  [IMPORT_TYPES_HISTORY["board-users"]]: "board users",
};

function dataTypeLabelFromMeta(record: UnknownRecord): string {
  const type = typeof record.data_type === "number" ? record.data_type : undefined;
  return type == null ? "data" : (DATA_TYPE_LABELS[type] ?? "data");
}

function csvEntityPhrase(record: UnknownRecord): string {
  return dataTypeLabelFromMeta(record);
}

function metaFileName(record: UnknownRecord): string | null {
  return typeof record.file_name === "string" && record.file_name.trim()
    ? record.file_name.trim()
    : null;
}

/**
 * CSV / board transfer body lines — parallel shape across import & export:
 *   Pending:  {Importing|Exporting} {target}[ from|as {file}]…
 *   Done:     {CSV|Board} {import|export} for {target}[ from|as {file}] …
 *   Failed:   {CSV|Board} {import|export} for {target}[ from|as {file}] has failed.
 * Eyebrow already shows Import / Export. `emphasize` bold entity + file in Updates.
 */
function csvTransferNarrative(
  record: UnknownRecord,
  kind: "import" | "export",
  status: string | null | undefined,
  options: { emphasize?: boolean } = {},
): string {
  if (status === "Cancelled") return "Cancelled";
  const mark = (value: string) => (options.emphasize ? emphasizePhrase(value) : value);
  const file = metaFileName(record);
  const entity = mark(csvEntityPhrase(record));
  const fileMarked = file ? mark(file) : null;
  const withFile =
    fileMarked == null
      ? entity
      : kind === "export"
        ? `${entity} as ${fileMarked}`
        : `${entity} from ${fileMarked}`;

  if (kind === "export") {
    switch (status) {
      case "Pending":
        return `Exporting ${withFile}…`;
      case "Failed":
        return `CSV export for ${withFile} has failed.`;
      default:
        return `CSV export for ${withFile} is ready to download.`;
    }
  }

  switch (status) {
    case "Pending":
      return `Importing ${withFile}…`;
    case "Failed":
      return `CSV import for ${withFile} has failed.`;
    default:
      return `CSV import for ${withFile} has succeeded.`;
  }
}

function csvTransferNarrativeFromNotification(
  notification: Notification,
  kind: "import" | "export",
): string {
  return csvTransferNarrative(meta(notification), kind, taskStatus(notification), {
    emphasize: true,
  });
}

function csvTransferNarrativeFromTask(
  task: AsyncTask,
  kind: "import" | "export",
): string {
  return csvTransferNarrative(task.meta_data as UnknownRecord, kind, task.status);
}

function undoImportNarrative(
  record: UnknownRecord,
  status: string | null | undefined,
  options: { emphasize?: boolean } = {},
): string {
  if (status === "Cancelled") return "Cancelled";
  const mark = (value: string) => (options.emphasize ? emphasizePhrase(value) : value);
  const entity = mark(csvEntityPhrase(record));
  const file = metaFileName(record);
  const target = file ? `${entity} from ${mark(file)}` : entity;
  switch (status) {
    case "Pending":
      return `Undoing CSV import for ${target}…`;
    case "Failed":
      return `Undo import for ${target} has failed.`;
    default:
      return `Undo import for ${target} has succeeded.`;
  }
}

function boardTransferNarrative(
  name: string | null,
  kind: "import" | "export",
  status: string | null | undefined,
): string {
  if (status === "Cancelled") return "Cancelled";
  const target = name ? emphasizePhrase(name) : null;
  if (kind === "export") {
    switch (status) {
      case "Pending":
        return target ? `Exporting board ${target}…` : "Exporting board…";
      case "Failed":
        return target
          ? `Board export for ${target} has failed.`
          : "Board export has failed.";
      default:
        return target
          ? `Board export for ${target} is ready to download.`
          : "Board export is ready to download.";
    }
  }
  switch (status) {
    case "Pending":
      return target ? `Importing board ${target}…` : "Importing board…";
    case "Failed":
      return target
        ? `Board import for ${target} has failed.`
        : "Board import has failed.";
    default:
      return target
        ? `Board import for ${target} has succeeded.`
        : "Board import has succeeded.";
  }
}

function toDownloadBlob(result: NotificationTaskResult, mimeType: string): Blob | null {
  if (result == null) return null;
  if (typeof Blob !== "undefined" && result instanceof Blob) return result;
  if (typeof result === "object" && "download_url" in result) return null;
  if (typeof result === "string") return new Blob([result], { type: mimeType });
  return new Blob([JSON.stringify(result, null, 2)], { type: mimeType });
}

async function downloadCompletedBoardExport(notification: Notification): Promise<void> {
  const task = notification.async_task;
  if (!task || task.status !== "Completed") return;
  try {
    await downloadBoardExportResult(
      task.id,
      boardExportFallbackFileName(stringMeta(notification, "board_name")),
    );
  } catch {
    showToast({
      msg: "Could not download the board export.",
      type: "error",
      duration: 3000,
    });
  }
}

async function downloadResultFile(
  notification: Notification,
  context: NotificationConfigContext,
  fileName: string,
  mimeType: string,
): Promise<void> {
  const task = notification.async_task;
  if (!task) return;
  const result = await context.getResult(task.id);
  if (context.downloadResult) {
    await context.downloadResult(notification, result, { fileName, mimeType });
    return;
  }
  if (typeof result === "object" && "download_url" in result) {
    const url = result.download_url;
    if (typeof url === "string") {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = typeof result.file_name === "string" ? result.file_name : fileName;
      anchor.click();
      return;
    }
  }
  const blob = toDownloadBlob(result, mimeType);
  if (blob) downloadFileFromData(blob, fileName);
}

async function openSiteUrlFromResult(
  notification: Notification,
  context: NotificationConfigContext,
  urlKey: "preview_url" | "publish_url",
): Promise<void> {
  const task = notification.async_task;
  if (!task || task.status === "Pending") return;
  try {
    const result = await context.getResult(task.id);
    const href = (result as UnknownRecord | null)?.[urlKey];
    if (typeof href === "string" && href.trim()) {
      window.open(href.trim(), "_blank", "noopener,noreferrer");
    }
  } catch {
    showToast({ msg: "Could not open the published site.", type: "error", duration: 3000 });
  }
}

async function runOpenAction(
  _notification: Notification,
  context: NotificationConfigContext,
  action: () => Awaitable<unknown>,
  options?: { keepPopupOpen?: boolean },
): Promise<void> {
  if (!options?.keepPopupOpen) {
    context.closePopup();
  }
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  await action();
}

function openAction(
  notification: Notification,
  context: NotificationConfigContext,
  options: {
    label: string;
    icon?: LucideIcon;
    run?: () => Awaitable<unknown>;
    target?: NavigateTarget;
    keepPopupOpen?: boolean;
  },
): NotificationActionDescriptor {
  return {
    key: "open",
    label: options.label,
    icon: options.icon ?? ArrowUpRight,
    variant: "outline",
    onClick: () =>
      runOpenAction(
        notification,
        context,
        () => {
          if (options.run) return options.run();
          return options.target ? context.navigate?.(options.target) : undefined;
        },
        { keepPopupOpen: options.keepPopupOpen },
      ),
  };
}

function cancelAction(
  notification: Notification,
  context: NotificationConfigContext,
): NotificationActionDescriptor | null {
  const task = notification.async_task;
  if (!task || task.status !== "Pending") return null;
  const currentUserId = context.currentUser?.id;
  const creatorAllowed = currentUserId == null || task.creator == null || task.creator === currentUserId;
  const permissionAllowed = context.permissions?.canCancelTask?.(notification) ?? true;
  const canCancel = creatorAllowed && permissionAllowed;
  return {
    key: "cancel",
    label: "Cancel task",
    icon: Ban,
    variant: "destructive",
    disabled: !canCancel,
    disabledReason: !creatorAllowed
      ? "Only the task creator can cancel"
      : !permissionAllowed
        ? "You do not have permission to cancel this task"
        : undefined,
    onClick: async () => {
      if (canCancel) await context.cancelTask(task.id);
    },
  };
}

function asyncStatusMessage(
  notification: Notification,
  messages: {
    getPendingMessage?: (task: AsyncTask) => string;
    getCompletedMessage?: (task: AsyncTask) => string;
    getResolvedMessage?: (task: AsyncTask) => string;
    getFailedMessage?: (task: AsyncTask) => string;
  },
): string {
  const task = notification.async_task;
  if (!task) return notification.description ?? "";
  switch (task.status) {
    case "Pending": return messages.getPendingMessage?.(task) ?? "Working…";
    case "Completed": return messages.getCompletedMessage?.(task) ?? "Completed";
    case "Resolved": return messages.getResolvedMessage?.(task) ?? messages.getCompletedMessage?.(task) ?? "Resolved";
    case "Failed": return messages.getFailedMessage?.(task) ?? "Failed";
    case "Cancelled": return "Cancelled";
  }
}

function asyncGetActions(
  notification: Notification,
  context: NotificationConfigContext,
  opts: {
    label: string;
    getNavigateTarget?: (notification: Notification) => NavigateTarget | null;
    primaryActionLabel?: string;
    primaryActionIcon?: LucideIcon;
    runPrimaryAction?: (notification: Notification, context: NotificationConfigContext) => Awaitable<unknown>;
    completionMode?: "auto" | "user";
    /** Keep Updates open when the completed primary action is a download. */
    keepPopupOpenOnDownload?: boolean;
  },
): NotificationActionDescriptor[] {
  const task = notification.async_task;
  if (!task) return [];
  const actions: NotificationActionDescriptor[] = [];
  const target = opts.getNavigateTarget?.(notification) ?? null;
  if (
    task.status !== "Failed" &&
    task.status !== "Cancelled" &&
    task.status !== "Resolved" &&
    (target || opts.runPrimaryAction)
  ) {
    const isPending = task.status === "Pending";
    actions.push(
      openAction(notification, context, {
        label: isPending
          ? `Go to ${opts.label}`
          : (opts.primaryActionLabel ?? "View"),
        icon: isPending ? ArrowUpRight : (opts.primaryActionIcon ?? ArrowUpRight),
        keepPopupOpen: Boolean(opts.keepPopupOpenOnDownload) && !isPending,
        run: opts.runPrimaryAction
          ? () => opts.runPrimaryAction?.(notification, context)
          : async () => {
              if (task.status === "Completed" && opts.completionMode === "auto") {
                try { await context.getResult(task.id); } catch { /* already retrieved */ }
              }
              if (target) await context.navigate?.(target);
            },
      }),
    );
  }
  const cancel = cancelAction(notification, context);
  if (cancel) actions.push(cancel);
  return actions;
}

function alertGetActions(
  notification: Notification,
  context: NotificationConfigContext,
  opts: { primaryActionLabel?: string; icon?: LucideIcon; openTarget?: (n: Notification) => NotificationOpenTarget },
): NotificationActionDescriptor[] {
  if (!opts.openTarget || !opts.primaryActionLabel) return [];
  return [
    openAction(notification, context, {
      label: opts.primaryActionLabel,
      icon: opts.icon,
      run: () => context.openNotification?.(notification, opts.openTarget?.(notification) ?? { kind: "entity" }),
    }),
  ];
}

const MY_SWIVU_TARGET: NavigateTarget = {
  viewType: VIEW_TYPES.mySwivu,
  viewName: "My Swivu",
};

/** Refresh board list and select the board named in async meta; fall back to My Swivu. */
async function openBoardFromMetaByName(
  notification: Notification,
  context: NotificationConfigContext,
): Promise<void> {
  const status = taskStatus(notification);

  if (status === "Pending") {
    await context.navigate?.(MY_SWIVU_TARGET);
    return;
  }

  const name = stringMeta(notification, "board_name");
  const boardManager = context.boardManager;
  const task = notification.async_task;

  if (status === "Completed" && task) {
    try {
      await context.getResult(task.id);
    } catch {
      /* already retrieved */
    }
  }

  if (!name || !boardManager?.changeSelectedBoard) {
    await context.navigate?.(MY_SWIVU_TARGET);
    return;
  }

  try {
    await boardManager.getRefreshUserBoardsListing?.();
  } catch {
    /* still try current store */
  }

  const store = boardManager.boardNameStore;
  const board =
    typeof store?.find === "function"
      ? store.find((record: { name?: string }) => record.name === name)
      : store?.records?.find?.((record: { name?: string }) => record.name === name);

  if (board) {
    boardManager.changeSelectedBoard(board);
    return;
  }

  await context.navigate?.(MY_SWIVU_TARGET);
}

// ─── Config Entries ────────────────────────────────────────────────────────────

export const NOTIFICATION_CONFIG: NotificationTypeEntry[] = [
  // ── Async Core ──
  {
    type: ASYNC_TASK_TYPES.BOARD_DUPLICATION,
    notifyType: "async",
    label: "Board duplication",
    icon: Copy,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "copy",
    categoryGroup: "task",
    classes: { bg: "bg-clay", text: "text-clay-foreground", ring: "ring-clay/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_DUPLICATION },
    getTitle: () => "",
    getDescription: (n) => {
      const name = resolveBoardLabel(n);
      return asyncStatusMessage(n, {
        getPendingMessage: () =>
          name
            ? `Duplicating board ${emphasizePhrase(name)}…`
            : "Duplicating board…",
        getCompletedMessage: () =>
          name
            ? `Board ${emphasizePhrase(name)} has been duplicated.`
            : "Board has been duplicated.",
        getFailedMessage: () =>
          name
            ? `Board duplication for ${emphasizePhrase(name)} has failed.`
            : "Board duplication has failed.",
      });
    },
    getStatusLabel: (n) => {
      switch (taskStatus(n)) {
        case "Pending": return "In progress";
        case "Completed": case "Resolved": return "Copy";
        case "Failed": return "Failed";
        case "Cancelled": return "Cancelled";
        default: return "Copy";
      }
    },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_DUPLICATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Board duplication",
      primaryActionLabel: "Open board",
      primaryActionIcon: LayoutGrid,
      runPrimaryAction: openBoardFromMetaByName,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openBoardFromMetaByName(n, ctx));
    },
    getPendingMessage: () => "Duplicating board…",
    getCompletedMessage: () => "Board has been duplicated.",
    getFailedMessage: () => "Board duplication has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.CSV_IMPORT,
    notifyType: "async",
    label: "CSV import",
    icon: FileUp,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "import",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.CSV_IMPORT },
    getTitle: (n) => csvTransferNarrativeFromNotification(n, "import"),
    getDescription: () => "",
    getStatusLabel: (n) => {
      switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; }
    },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.CSV_IMPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, { label: "CSV import", completionMode: "auto" }),
    onOpen: () => {},
    getPendingMessage: (t) => csvTransferNarrativeFromTask(t, "import"),
    getCompletedMessage: (t) => csvTransferNarrativeFromTask(t, "import"),
    getFailedMessage: (t) => csvTransferNarrativeFromTask(t, "import"),
  },
  {
    type: ASYNC_TASK_TYPES.CSV_EXPORT,
    notifyType: "async",
    label: "CSV export",
    icon: FileDown,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "import",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.CSV_EXPORT },
    getTitle: (n) => csvTransferNarrativeFromNotification(n, "export"),
    getDescription: () => "",
    getStatusLabel: (n) => {
      switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Export"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Export"; }
    },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.CSV_EXPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "CSV export",
      primaryActionLabel: "Download CSV",
      primaryActionIcon: FileDown,
      runPrimaryAction: (notification, context) =>
        downloadResultFile(notification, context, stringMeta(notification, "file_name") ?? "export.csv", "text/csv"),
      completionMode: "auto",
      keepPopupOpenOnDownload: true,
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () =>
        downloadResultFile(n, ctx, stringMeta(n, "file_name") ?? "export.csv", "text/csv"));
    },
    getPendingMessage: (t) => csvTransferNarrativeFromTask(t, "export"),
    getCompletedMessage: (t) => csvTransferNarrativeFromTask(t, "export"),
    getFailedMessage: (t) => csvTransferNarrativeFromTask(t, "export"),
  },
  {
    type: ASYNC_TASK_TYPES.UNDO_CSV_IMPORT,
    notifyType: "async",
    label: "Undo import",
    icon: Undo2,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "import",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.UNDO_CSV_IMPORT },
    getTitle: (n) => undoImportNarrative(meta(n), taskStatus(n), { emphasize: true }),
    getDescription: () => "",
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.UNDO_CSV_IMPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, { label: "Undo import", completionMode: "auto" }),
    onOpen: () => {},
    getPendingMessage: (t) => undoImportNarrative(t.meta_data as UnknownRecord, t.status),
    getCompletedMessage: (t) => undoImportNarrative(t.meta_data as UnknownRecord, t.status),
    getFailedMessage: (t) => undoImportNarrative(t.meta_data as UnknownRecord, t.status),
  },
  {
    type: ASYNC_TASK_TYPES.DEMO_BOARD_CREATION,
    notifyType: "async",
    label: "Test drive",
    icon: WandSparkles,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "general",
    categoryGroup: "task",
    classes: { bg: "bg-action", text: "text-action-foreground", ring: "ring-action/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.DEMO_BOARD_CREATION },
    getTitle: () => "",
    getDescription: (n) => {
      const name = resolveBoardLabel(n);
      return asyncStatusMessage(n, {
        getPendingMessage: () =>
          name
            ? `Creating test drive ${emphasizePhrase(name)}…`
            : "Creating test drive…",
        getCompletedMessage: () =>
          name
            ? `Test drive ${emphasizePhrase(name)} is ready.`
            : "Test drive is ready.",
        getFailedMessage: () =>
          name
            ? `Test drive creation for ${emphasizePhrase(name)} has failed.`
            : "Test drive creation has failed.",
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Board"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Board"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.DEMO_BOARD_CREATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Test drive",
      primaryActionLabel: "Open board",
      primaryActionIcon: LayoutGrid,
      runPrimaryAction: openBoardFromMetaByName,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openBoardFromMetaByName(n, ctx));
    },
    getPendingMessage: () => "Creating test drive…",
    getCompletedMessage: () => "Test drive is ready.",
    getFailedMessage: () => "Test drive creation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_IMPORT,
    notifyType: "async",
    label: "Board import",
    icon: FileUp,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "import",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_IMPORT },
    getTitle: () => "",
    getDescription: (n) =>
      boardTransferNarrative(resolveBoardLabel(n), "import", taskStatus(n)),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_IMPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Board import",
      primaryActionLabel: "Open import",
      primaryActionIcon: FileUp,
      runPrimaryAction: openBoardFromMetaByName,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openBoardFromMetaByName(n, ctx));
    },
    getPendingMessage: () => "Importing board…",
    getCompletedMessage: () => "Board import has succeeded.",
    getFailedMessage: () => "Board import has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_EXPORT,
    notifyType: "async",
    label: "Board export",
    icon: FileDown,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "import",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_EXPORT },
    getTitle: () => "",
    getDescription: (n) =>
      boardTransferNarrative(resolveBoardLabel(n), "export", taskStatus(n)),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Export"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Export"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_EXPORT}`,
    getActions: (n, ctx) => {
      const actions: NotificationActionDescriptor[] = [];
      if (taskStatus(n) === "Completed" && n.async_task) {
        actions.push(
          openAction(n, ctx, {
            label: "Download export",
            icon: FileDown,
            keepPopupOpen: true,
            run: () => downloadCompletedBoardExport(n),
          }),
        );
      }
      const cancel = cancelAction(n, ctx);
      if (cancel) actions.push(cancel);
      return actions;
    },
    onOpen: (n, ctx) => {
      if (taskStatus(n) !== "Completed" || n.async_task == null) return;
      void runOpenAction(n, ctx, () => downloadCompletedBoardExport(n));
    },
    getPendingMessage: () => "Exporting board…",
    getCompletedMessage: () => "Board export is ready to download.",
    getFailedMessage: () => "Board export has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.INCENTIVE_EVALUATION,
    notifyType: "async",
    label: "Incentive evaluation",
    icon: Trophy,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "incentive",
    categoryGroup: "task",
    classes: { bg: "bg-ochre", text: "text-ochre-foreground", ring: "ring-ochre/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.INCENTIVE_EVALUATION },
    getTitle: () => "",
    getDescription: (n, ctx) => {
      const board = resolveBoardLabel(n);
      const name =
        resolveLabel(ctx, "incentive", numberMeta(n, "incentive_id"))
        ?? stringMeta(n, "incentive_name");
      return asyncStatusMessage(n, {
        getPendingMessage: () => {
          if (name && board) {
            return `Evaluating incentive ${emphasizePhrase(name)} for ${emphasizePhrase(board)}…`;
          }
          if (name) return `Evaluating incentive ${emphasizePhrase(name)}…`;
          if (board) return `Evaluating incentives for ${emphasizePhrase(board)}…`;
          return "Evaluating incentive outcomes…";
        },
        getCompletedMessage: () => {
          if (name && board) {
            return `Incentive ${emphasizePhrase(name)} for ${emphasizePhrase(board)} is ready.`;
          }
          if (name) return `Incentive ${emphasizePhrase(name)} is ready.`;
          if (board) return `Incentive evaluation for ${emphasizePhrase(board)} is ready.`;
          return "Incentive evaluation is ready.";
        },
        getFailedMessage: () => {
          if (name && board) {
            return `Incentive evaluation for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} has failed.`;
          }
          if (name) return `Incentive evaluation for ${emphasizePhrase(name)} has failed.`;
          if (board) return `Incentive evaluation for ${emphasizePhrase(board)} has failed.`;
          return "Incentive evaluation has failed.";
        },
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Incentive"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Incentive"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.INCENTIVE_EVALUATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Incentive evaluation",
      primaryActionLabel: "View outcomes",
      primaryActionIcon: Trophy,
      runPrimaryAction: async (notification, context) => {
        const task = notification.async_task;
        if (!task) return;
        const result = await context.getResult(task.id);
        await context.openIncentiveResults?.(notification, result);
      },
      completionMode: "user",
    }),
    onOpen: async (n, ctx) => {
      const task = n.async_task;
      if (!task) return;
      await runOpenAction(n, ctx, async () => {
        const result = await ctx.getResult(task.id);
        await ctx.openIncentiveResults?.(n, result);
      });
    },
    getPendingMessage: () => "Evaluating incentive outcomes…",
    getCompletedMessage: () => "Incentive evaluation is ready.",
    getFailedMessage: () => "Incentive evaluation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.BULK_ENTITY_COPY,
    notifyType: "async",
    label: "Bulk copy",
    icon: Copy,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "copy",
    categoryGroup: "task",
    classes: { bg: "bg-clay", text: "text-clay-foreground", ring: "ring-clay/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BULK_ENTITY_COPY },
    getTitle: () => "",
    getDescription: (n, ctx) => {
      const count = numberMeta(n, "entity_count");
      const entityType = stringMeta(n, "entity_type") ?? "entities";
      const entityPhrase =
        count != null && count > 0
          ? `${emphasizePhrase(String(count))} ${entityType}`
          : emphasizePhrase(entityType);
      const destination =
        resolveLabel(ctx, "board", numberMeta(n, "destination_board_id"));
      const toBoard = destination
        ? ` to ${emphasizePhrase(destination)}`
        : "";
      return asyncStatusMessage(n, {
        getPendingMessage: () => `Copying ${entityPhrase}${toBoard}…`,
        getCompletedMessage: () => `${entityPhrase} copied successfully${toBoard}.`,
        getFailedMessage: () => `Copy of ${entityPhrase}${toBoard} has failed.`,
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Copy"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Copy"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BULK_ENTITY_COPY}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, { label: "Bulk copy", completionMode: "user" }),
    onOpen: () => {},
    getPendingMessage: () => "Copying entities…",
    getCompletedMessage: () => "Entities copied successfully.",
    getFailedMessage: () => "Entity copy has failed.",
  },
  // ── Async Marketing ──
  {
    type: ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION,
    notifyType: "async",
    label: "Marketing manifest",
    icon: FileText,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION },
    getTitle: () => "",
    getDescription: (n) => {
      const board =
        pickTrimmed(n.board_name)
        ?? stringMeta(n, "board_name");
      return asyncStatusMessage(n, {
        getPendingMessage: () =>
          board
            ? `Generating marketing manifest for ${emphasizePhrase(board)}…`
            : "Generating marketing manifest…",
        getCompletedMessage: () =>
          board
            ? `Marketing manifest for ${emphasizePhrase(board)} is ready.`
            : "Marketing manifest is ready.",
        getFailedMessage: () =>
          board
            ? `Marketing manifest generation for ${emphasizePhrase(board)} has failed.`
            : "Marketing manifest generation has failed.",
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Marketing manifest",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: numberMeta(notification, "board_context_id") != null ? { contextId: numberMeta(notification, "board_context_id") } : {} }),
      primaryActionLabel: "Open manifest",
      primaryActionIcon: FileText,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: numberMeta(n, "board_context_id") != null ? { contextId: numberMeta(n, "board_context_id") } : {} })); },
    getPendingMessage: () => "Generating marketing manifest…",
    getCompletedMessage: () => "Marketing manifest is ready.",
    getFailedMessage: () => "Marketing manifest generation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION,
    notifyType: "async",
    label: "Marketing tags",
    icon: Tags,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION },
    getTitle: () => "",
    getDescription: (n) => {
      const board =
        pickTrimmed(n.board_name)
        ?? stringMeta(n, "board_name");
      return asyncStatusMessage(n, {
        getPendingMessage: () =>
          board
            ? `Generating marketing tags for ${emphasizePhrase(board)}…`
            : "Generating marketing tags…",
        getCompletedMessage: () =>
          board
            ? `Marketing tags for ${emphasizePhrase(board)} are ready.`
            : "Marketing tags are ready.",
        getFailedMessage: () =>
          board
            ? `Marketing tags generation for ${emphasizePhrase(board)} has failed.`
            : "Marketing tags generation has failed.",
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Marketing tags",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: { openTagReview: true, ...(numberMeta(notification, "board_context_id") != null ? { contextId: numberMeta(notification, "board_context_id") } : {}) } }),
      primaryActionLabel: "View tags",
      primaryActionIcon: Tags,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: { openTagReview: true, ...(numberMeta(n, "board_context_id") != null ? { contextId: numberMeta(n, "board_context_id") } : {}) } })); },
    getPendingMessage: () => "Generating marketing tags…",
    getCompletedMessage: () => "Marketing tags are ready.",
    getFailedMessage: () => "Marketing tags generation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION,
    notifyType: "async",
    label: "Work file",
    icon: FileStack,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION },
    getTitle: () => "",
    getDescription: (n, ctx) => {
      const board =
        pickTrimmed(n.board_name)
        ?? stringMeta(n, "board_name");
      const name =
        resolveLabel(ctx, "work-file", numberMeta(n, "work_file_id"))
        ?? stringMeta(n, "name");
      return asyncStatusMessage(n, {
        getPendingMessage: () => {
          if (name && board) {
            return `Generating work file ${emphasizePhrase(name)} for ${emphasizePhrase(board)}…`;
          }
          if (name) return `Generating work file ${emphasizePhrase(name)}…`;
          if (board) return `Generating a marketing work file for ${emphasizePhrase(board)}…`;
          return "Generating marketing work file…";
        },
        getCompletedMessage: () => {
          if (name && board) {
            return `Work file ${emphasizePhrase(name)} for ${emphasizePhrase(board)} is ready.`;
          }
          if (name) return `Work file ${emphasizePhrase(name)} is ready.`;
          if (board) return `Marketing work file for ${emphasizePhrase(board)} is ready.`;
          return "Marketing work file is ready.";
        },
        getFailedMessage: () => {
          if (name && board) {
            return `Work file generation for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} has failed.`;
          }
          if (name) return `Work file generation for ${emphasizePhrase(name)} has failed.`;
          if (board) return `Work file generation for ${emphasizePhrase(board)} has failed.`;
          return "Work file generation has failed.";
        },
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Work file",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(notification, "work_file_id") != null ? { workFileId: numberMeta(notification, "work_file_id") } : {} }),
      primaryActionLabel: "Open work file",
      primaryActionIcon: FileStack,
      completionMode: "user",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(n, "work_file_id") != null ? { workFileId: numberMeta(n, "work_file_id") } : {} })); },
    getPendingMessage: () => "Generating marketing work file…",
    getCompletedMessage: () => "Marketing work file is ready.",
    getFailedMessage: () => "Work file generation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION,
    notifyType: "async",
    label: "Page generation",
    icon: Globe,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION },
    getTitle: () => "",
    getDescription: (n, ctx) => {
      const board =
        pickTrimmed(n.board_name)
        ?? stringMeta(n, "board_name");
      const workFileName = resolveLabel(ctx, "work-file", numberMeta(n, "work_file_id"));
      const pageName = resolveLabel(ctx, "marketing-page", numberMeta(n, "marketing_page_id"));
      return asyncStatusMessage(n, {
        getPendingMessage: () => {
          if (workFileName && board) {
            return `Publishing marketing page from ${emphasizePhrase(workFileName)} for ${emphasizePhrase(board)}…`;
          }
          if (workFileName) return `Publishing marketing page from ${emphasizePhrase(workFileName)}…`;
          if (board) return `Publishing marketing page for ${emphasizePhrase(board)}…`;
          return "Publishing marketing page…";
        },
        getCompletedMessage: () => {
          if (pageName && board) {
            return `Marketing page ${emphasizePhrase(pageName)} for ${emphasizePhrase(board)} is ready.`;
          }
          if (workFileName && board) {
            return `Marketing page from ${emphasizePhrase(workFileName)} for ${emphasizePhrase(board)} is ready.`;
          }
          if (pageName) return `Marketing page ${emphasizePhrase(pageName)} is ready.`;
          if (workFileName) return `Marketing page from ${emphasizePhrase(workFileName)} is ready.`;
          if (board) return `Marketing page for ${emphasizePhrase(board)} is ready.`;
          return "Marketing page is ready.";
        },
        getFailedMessage: () => {
          if (workFileName && board) {
            return `Marketing page generation from ${emphasizePhrase(workFileName)} for ${emphasizePhrase(board)} has failed.`;
          }
          if (workFileName) return `Marketing page generation from ${emphasizePhrase(workFileName)} has failed.`;
          if (board) return `Marketing page generation for ${emphasizePhrase(board)} has failed.`;
          return "Marketing page generation has failed.";
        },
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Page generation",
      getNavigateTarget: (notification) =>
        numberMeta(notification, "marketing_page_id") != null
          ? { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages", otherParams: { marketingPageId: numberMeta(notification, "marketing_page_id") } }
          : numberMeta(notification, "work_file_id") != null
            ? { viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: { workFileId: numberMeta(notification, "work_file_id") } }
            : { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages" },
      primaryActionLabel: "Open page",
      primaryActionIcon: ArrowUpRight,
      completionMode: "user",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => {
        const target = numberMeta(n, "marketing_page_id") != null
          ? { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages", otherParams: { marketingPageId: numberMeta(n, "marketing_page_id") } }
          : numberMeta(n, "work_file_id") != null
            ? { viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: { workFileId: numberMeta(n, "work_file_id") } }
            : { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages" };
        return ctx.navigate?.(target);
      });
    },
    getPendingMessage: () => "Publishing marketing page…",
    getCompletedMessage: () => "Marketing page is ready.",
    getFailedMessage: () => "Marketing page generation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.SITE_PUBLISH,
    notifyType: "async",
    label: "Site publish",
    icon: Globe,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.SITE_PUBLISH },
    getTitle: () => "",
    getDescription: (n) => {
      const board = resolveBoardLabel(n);
      return asyncStatusMessage(n, {
        getPendingMessage: () =>
          board
            ? `Publishing site for ${emphasizePhrase(board)}…`
            : "Publishing site…",
        getCompletedMessage: () =>
          board
            ? `Site for ${emphasizePhrase(board)} has been published.`
            : "Site has been published.",
        getFailedMessage: () =>
          board
            ? `Site publish for ${emphasizePhrase(board)} has failed.`
            : "Site publish has failed.",
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.SITE_PUBLISH}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Site publish",
      primaryActionLabel: "View site",
      primaryActionIcon: Globe,
      completionMode: "auto",
      ...(taskStatus(n) !== "Pending"
        ? { runPrimaryAction: (notification, context) => openSiteUrlFromResult(notification, context, "preview_url") }
        : {}),
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openSiteUrlFromResult(n, ctx, "preview_url"));
    },
    getPendingMessage: () => "Publishing site…",
    getCompletedMessage: () => "Site has been published.",
    getFailedMessage: () => "Site publish has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY,
    notifyType: "async",
    label: "Custom domain deploy",
    icon: Globe,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY },
    getTitle: () => "",
    getDescription: (n) => {
      const board = resolveBoardLabel(n);
      const domain = stringMeta(n, "domain") ?? stringMeta(n, "custom_domain");
      return asyncStatusMessage(n, {
        getPendingMessage: () => {
          if (domain && board) {
            return `Deploying ${emphasizePhrase(domain)} for ${emphasizePhrase(board)}…`;
          }
          if (domain) return `Deploying ${emphasizePhrase(domain)}…`;
          if (board) return `Deploying custom domain for ${emphasizePhrase(board)}…`;
          return "Deploying custom domain…";
        },
        getCompletedMessage: () => {
          if (domain && board) {
            return `Custom domain ${emphasizePhrase(domain)} for ${emphasizePhrase(board)} is live.`;
          }
          if (domain) return `Custom domain ${emphasizePhrase(domain)} is live.`;
          if (board) return `Custom domain for ${emphasizePhrase(board)} is live.`;
          return "Custom domain deploy is complete.";
        },
        getFailedMessage: () => {
          if (domain && board) {
            return `Custom domain deploy for ${emphasizePhrase(domain)} on ${emphasizePhrase(board)} has failed.`;
          }
          if (domain) return `Custom domain deploy for ${emphasizePhrase(domain)} has failed.`;
          if (board) return `Custom domain deploy for ${emphasizePhrase(board)} has failed.`;
          return "Custom domain deploy has failed.";
        },
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Custom domain deploy",
      primaryActionLabel: "View site",
      primaryActionIcon: Globe,
      completionMode: "auto",
      ...(taskStatus(n) !== "Pending"
        ? { runPrimaryAction: (notification, context) => openSiteUrlFromResult(notification, context, "publish_url") }
        : {}),
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openSiteUrlFromResult(n, ctx, "publish_url"));
    },
    getPendingMessage: () => "Deploying custom domain…",
    getCompletedMessage: () => "Custom domain deploy is complete.",
    getFailedMessage: () => "Custom domain deploy has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION,
    notifyType: "async",
    label: "Document generation",
    icon: FileText,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION },
    getTitle: () => "",
    getDescription: (n, ctx) => {
      const board =
        pickTrimmed(n.board_name)
        ?? stringMeta(n, "board_name");
      const name =
        resolveLabel(ctx, "work-file", numberMeta(n, "work_file_id"))
        ?? stringMeta(n, "name");
      const format = stringMeta(n, "format")?.toUpperCase() ?? null;
      const docLabel = format ? `${format} document` : "document";
      return asyncStatusMessage(n, {
        getPendingMessage: () => {
          if (name && board) {
            return `Generating ${docLabel} ${emphasizePhrase(name)} for ${emphasizePhrase(board)}…`;
          }
          if (name) return `Generating ${docLabel} ${emphasizePhrase(name)}…`;
          if (board) return `Generating ${docLabel} for ${emphasizePhrase(board)}…`;
          return `Generating ${docLabel}…`;
        },
        getCompletedMessage: () => {
          if (name && board) {
            return `${format ? `${format} document` : "Document"} ${emphasizePhrase(name)} for ${emphasizePhrase(board)} is ready.`;
          }
          if (name) return `${format ? `${format} document` : "Document"} ${emphasizePhrase(name)} is ready.`;
          if (board) return `${format ? `${format} document` : "Document"} for ${emphasizePhrase(board)} is ready.`;
          return `${format ? `${format} document` : "Document"} is ready.`;
        },
        getFailedMessage: () => {
          if (name && board) {
            return `${format ? `${format} document` : "Document"} generation for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} has failed.`;
          }
          if (name) return `${format ? `${format} document` : "Document"} generation for ${emphasizePhrase(name)} has failed.`;
          if (board) return `${format ? `${format} document` : "Document"} generation for ${emphasizePhrase(board)} has failed.`;
          return `${format ? `${format} document` : "Document"} generation has failed.`;
        },
      });
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Document generation",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(notification, "work_file_id") != null ? { workFileId: numberMeta(notification, "work_file_id") } : {} }),
      primaryActionLabel: "Open document",
      primaryActionIcon: FileText,
      completionMode: "user",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(n, "work_file_id") != null ? { workFileId: numberMeta(n, "work_file_id") } : {} })); },
    getPendingMessage: () => "Generating document…",
    getCompletedMessage: () => "Document is ready.",
    getFailedMessage: () => "Document generation has failed.",
  },
  // ── Async Context ──
  {
    type: ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
    notifyType: "async",
    label: "Context questions",
    icon: Layers,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "context",
    categoryGroup: "task",
    classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION },
    getTitle: () => "",
    getDescription: (n, ctx) => asyncStatusMessage(n, {
      getPendingMessage: () => {
        const board = pickTrimmed(n.board_name);
        return board
          ? `Generating context questions for ${emphasizePhrase(board)}…`
          : "Generating context questions…";
      },
      getCompletedMessage: () => {
        const board = pickTrimmed(n.board_name);
        const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id"));
        if (name && board) {
          return `Context questions for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} are ready.`;
        }
        if (board) return `Context questions for ${emphasizePhrase(board)} are ready.`;
        if (name) return `Context questions for ${emphasizePhrase(name)} are ready.`;
        return "Context questions are ready.";
      },
      getFailedMessage: () => {
        const board = pickTrimmed(n.board_name);
        const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id"));
        if (name && board) {
          return `Context questions generation for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} has failed.`;
        }
        if (name) return `Context questions generation for ${emphasizePhrase(name)} has failed.`;
        if (board) return `Context questions generation for ${emphasizePhrase(board)} has failed.`;
        return "Context questions generation has failed.";
      },
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Context"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Context"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Context questions",
      getNavigateTarget: (notification) => numberMeta(notification, "board_context_id") == null ? null : { viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: numberMeta(notification, "board_context_id"), openWizard: true } },
      primaryActionLabel: "Open context",
      primaryActionIcon: Layers,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { const cId = numberMeta(n, "board_context_id"); if (cId != null) runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: cId, openWizard: true } })); },
    getPendingMessage: () => "Generating context questions…",
    getCompletedMessage: () => "Context questions are ready.",
    getFailedMessage: () => "Context questions generation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
    notifyType: "async",
    label: "Context build",
    icon: Layers,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "context",
    categoryGroup: "task",
    classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION },
    getTitle: () => "",
    getDescription: (n, ctx) => asyncStatusMessage(n, {
      getPendingMessage: () => {
        const board = pickTrimmed(n.board_name);
        return board
          ? `Building board context for ${emphasizePhrase(board)}…`
          : "Building board context…";
      },
      getCompletedMessage: () => {
        const board = pickTrimmed(n.board_name);
        const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id"));
        if (name && board) {
          return `Board context ${emphasizePhrase(name)} for ${emphasizePhrase(board)} is ready.`;
        }
        if (board) return `Board context for ${emphasizePhrase(board)} is ready.`;
        if (name) return `Board context ${emphasizePhrase(name)} is ready.`;
        return "Board context is ready.";
      },
      getFailedMessage: () => {
        const board = pickTrimmed(n.board_name);
        const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id"));
        if (name && board) {
          return `Board context generation for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} has failed.`;
        }
        if (board) return `Board context generation for ${emphasizePhrase(board)} has failed.`;
        if (name) return `Board context generation for ${emphasizePhrase(name)} has failed.`;
        return "Board context generation has failed.";
      },
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Context"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Context"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Context build",
      getNavigateTarget: (notification) => numberMeta(notification, "board_context_id") == null ? null : { viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: numberMeta(notification, "board_context_id") } },
      primaryActionLabel: "Open context",
      primaryActionIcon: Layers,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { const cId = numberMeta(n, "board_context_id"); if (cId != null) runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: cId } })); },
    getPendingMessage: () => "Building board context…",
    getCompletedMessage: () => "Board context is ready.",
    getFailedMessage: () => "Board context generation has failed.",
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
    notifyType: "async",
    label: "Context suggestions",
    icon: Sparkles,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "context",
    categoryGroup: "task",
    classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION },
    getTitle: () => "",
    getDescription: (n, ctx) => asyncStatusMessage(n, {
      getPendingMessage: () => {
        const board = pickTrimmed(n.board_name);
        return board
          ? `Generating context suggestions for ${emphasizePhrase(board)}…`
          : "Generating context suggestions…";
      },
      getCompletedMessage: () => {
        const board = pickTrimmed(n.board_name);
        const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id"));
        if (name && board) {
          return `Context suggestions for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} are ready.`;
        }
        if (board) return `Context suggestions for ${emphasizePhrase(board)} are ready.`;
        if (name) return `Context suggestions for ${emphasizePhrase(name)} are ready.`;
        return "Context suggestions are ready.";
      },
      getFailedMessage: () => {
        const board = pickTrimmed(n.board_name);
        const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id"));
        if (name && board) {
          return `Context suggestion generation for ${emphasizePhrase(name)} on ${emphasizePhrase(board)} has failed.`;
        }
        if (board) return `Context suggestion generation for ${emphasizePhrase(board)} has failed.`;
        if (name) return `Context suggestion generation for ${emphasizePhrase(name)} has failed.`;
        return "Context suggestion generation has failed.";
      },
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Context"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Context"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Context suggestions",
      getNavigateTarget: (notification) => numberMeta(notification, "board_context_id") == null ? null : { viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: numberMeta(notification, "board_context_id") } },
      primaryActionLabel: "Open context",
      primaryActionIcon: Sparkles,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { const cId = numberMeta(n, "board_context_id"); if (cId != null) runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: cId } })); },
    getPendingMessage: () => "Generating context suggestions…",
    getCompletedMessage: () => "Context suggestions are ready.",
    getFailedMessage: () => "Context suggestion generation has failed.",
  },
  // ── Alerts ──
  {
    type: ALERT_TYPES.REMINDER, notifyType: "notification", label: "Reminder", icon: Bell, stream: "notification", categoryKey: "reminders", categoryGroup: "alert",
    classes: { bg: "bg-warning", text: "text-warning-foreground", ring: "ring-warning/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.REMINDER },
    getTitle: () => "",
    getDescription: (n) => reminderDescription(n),
    getStatusLabel: () => "Reminders",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open reminder", icon: Bell, openTarget: () => ({ kind: "reminder-detail" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "reminder-detail" })); },
  },
  {
    type: ALERT_TYPES.Schedule, notifyType: "notification", label: "Schedule change", icon: CalendarClock, stream: "notification", categoryKey: "schedule", categoryGroup: "alert",
    classes: { bg: "bg-rose", text: "text-rose-foreground", ring: "ring-rose/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.Schedule },
    getTitle: () => "",
    getDescription: (n) => scheduleDescription(n),
    getStatusLabel: () => "Schedule",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open schedule", icon: CalendarClock, openTarget: () => ({ kind: "schedule" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "schedule" })); },
  },
  {
    type: ALERT_TYPES.Checklist, notifyType: "notification", label: "Checklist", icon: CheckSquare, stream: "notification", categoryKey: "nudges", categoryGroup: "alert",
    classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.Checklist },
    getTitle: () => "",
    getDescription: (n) => nudgeDescription(n),
    getStatusLabel: () => "Nudges",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open Checklist", icon: CheckSquare, openTarget: (notification) => ({ kind: "checklist", checklistId: notification.checklist ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "checklist", checklistId: n.checklist ?? undefined })); },
  },
  {
    type: ALERT_TYPES.Nudge, notifyType: "notification", label: "Nudge", icon: ListChecks, stream: "notification", categoryKey: "nudges", categoryGroup: "alert",
    classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.Nudge },
    getTitle: () => "",
    getDescription: (n) => nudgeDescription(n),
    getStatusLabel: () => "Nudges",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open Checklist", icon: ListChecks, openTarget: (notification) => ({ kind: "checklist", checklistId: notification.checklist ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "checklist", checklistId: n.checklist ?? undefined })); },
  },
  {
    type: ALERT_TYPES.BoardAlert, notifyType: "notification", label: "Board alert", icon: ShieldAlert, stream: "notification", categoryKey: "board_alert", categoryGroup: "alert",
    classes: { bg: "bg-destructive", text: "text-destructive-foreground", ring: "ring-destructive/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.BoardAlert },
    getTitle: () => "",
    getDescription: (n) => boardAlertDescription(n),
    getStatusLabel: () => "Board alert",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: () => [],
    onOpen: () => {},
  },
  {
    type: ALERT_TYPES.EntityStatus, notifyType: "notification", label: "Status", icon: ArrowLeftRight, stream: "notification", categoryKey: "status", categoryGroup: "alert",
    classes: { bg: "bg-slate", text: "text-slate-foreground", ring: "ring-slate/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.EntityStatus },
    getTitle: () => "",
    getDescription: (n) => statusDescription(n),
    getStatusLabel: () => "Status",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open", icon: ArrowUpRight, openTarget: () => ({ kind: "entity" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "entity" })); },
  },
  {
    type: ALERT_TYPES.GENERAL, notifyType: "notification", label: "General", icon: Megaphone, stream: "notification", categoryKey: "general", categoryGroup: "alert",
    classes: { bg: "bg-chart-3", text: "text-white", ring: "ring-chart-3/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.GENERAL },
    // API often sends field-change alerts (Phase, Tagged, …) as GENERAL with category "status".
    getTitle: () => "",
    getDescription: (n) =>
      isStatusCategoryNotification(n)
        ? statusDescription(n)
        : generalDescription(n),
    getStatusLabel: () => "General",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open", icon: Megaphone, openTarget: () => ({ kind: "entity" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "entity" })); },
  },
  {
    type: ALERT_TYPES.NOTE, notifyType: "notification", label: "Note", icon: StickyNote, stream: "notification", categoryKey: "notes", categoryGroup: "alert",
    classes: { bg: "bg-ochre", text: "text-ochre-foreground", ring: "ring-ochre/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.NOTE },
    getTitle: () => "",
    getDescription: (n) => noteDescription(n),
    getStatusLabel: () => "Notes",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open note", icon: StickyNote, openTarget: (notification) => ({ kind: "note", noteId: notification.note ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "note", noteId: n.note ?? undefined })); },
  },
  {
    type: ALERT_TYPES.FILE, notifyType: "notification", label: "File", icon: FileText, stream: "notification", categoryKey: "file", categoryGroup: "alert",
    classes: { bg: "bg-stone-500", text: "text-white", ring: "ring-stone-500/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.FILE },
    getTitle: () => "",
    getDescription: (n) => fileDescription(n),
    getStatusLabel: () => "Files",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open file", icon: FileText, openTarget: (notification) => ({ kind: "file", fileId: notification.file ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "file", fileId: n.file ?? undefined })); },
  },
  {
    type: ALERT_TYPES.LEADS, notifyType: "notification", label: "Leads", icon: Target, stream: "notification", categoryKey: "leads", categoryGroup: "alert",
    classes: { bg: "bg-success", text: "text-success-foreground", ring: "ring-success/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.LEADS },
    getTitle: () => "",
    getDescription: (n) => leadsDescription(n),
    getStatusLabel: () => "Leads",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open lead", icon: Target, openTarget: (notification) => ({ kind: "lead", leadId: notification.data_type ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "lead", leadId: n.data_type ?? undefined })); },
  },
];

// ─── Fallback Entry ────────────────────────────────────────────────────────────

export const FALLBACK_ENTRY: NotificationTypeEntry = {
  notifyType: "notification",
  type: -1,
  label: "Update",
  icon: AlertTriangle,
  stream: "notification",
  categoryKey: "general",
  categoryGroup: "alert",
  classes: { bg: "bg-muted", text: "text-muted-foreground", ring: "ring-muted/20" },
  filter: { notify_type: "notification", type: -1 as NotificationFilterType },
  getTitle: (n) => n.title?.trim() || n.alert_tag?.trim() || "Update",
  getDescription: (n) => n.description?.trim() || "Notification details unavailable",
  getStatusLabel: (n) => n.async_task?.status ?? n.status ?? "Update",
  getActorName: actorName,
  notableWhen: (n) => n.status === "Failed" || !n.is_seen,
  aggregateKey: (n) => `unknown:${n.async_task?.type ?? n.alert_type ?? "type"}`,
  getActions: () => [],
  onOpen: () => {},
};

// ─── Lookup Helpers ────────────────────────────────────────────────────────────

export function getNotificationConfig(notification: Notification): NotificationTypeEntry {
  const entry = notification.async_task != null
    ? NOTIFICATION_CONFIG.find((e) => e.notifyType === "async" && e.type === notification.async_task!.type)
    : NOTIFICATION_CONFIG.find((e) => e.notifyType === "notification" && e.type === notification.alert_type);
  return entry ?? { ...FALLBACK_ENTRY, type: notification.async_task?.type ?? notification.alert_type };
}

/** Whether a notification should appear in Updates list surfaces. */
export function shouldIncludeNotificationInUpdates(notification: Notification): boolean {
  const entry = getNotificationConfig(notification);
  return entry.includeInUpdates?.(notification) ?? true;
}

export function getAsyncNotificationConfig(
  type: AsyncTaskTypeId,
): NotificationTypeEntry & { getPendingMessage: (task: AsyncTask) => string } {
  const entry = NOTIFICATION_CONFIG.find((e) => e.notifyType === "async" && e.type === type);
  if (entry) return entry as NotificationTypeEntry & { getPendingMessage: (task: AsyncTask) => string };
  return {
    ...FALLBACK_ENTRY,
    notifyType: "async",
    type,
    filter: { notify_type: "async", type },
    stream: "system",
    getPendingMessage: () => "Working…",
    getCompletedMessage: () => "Complete",
    getResolvedMessage: () => "Resolved",
    getFailedMessage: () => "Failed",
  } as NotificationTypeEntry & { getPendingMessage: (task: AsyncTask) => string };
}

// ─── Category Metadata ─────────────────────────────────────────────────────────

interface CategoryMeta {
  key: string;
  group: NotificationCategoryGroup;
  label: string;
  caption: string;
  clauseSingular: string;
  clausePlural: string;
  icon: LucideIcon;
  classes: { bg: string; text: string; ring: string };
  order: number;
}

const CATEGORIES: CategoryMeta[] = [
  { key: "marketing", group: "task", label: "Marketing", caption: "Updates from your marketing site and pages", clauseSingular: "marketing update", clausePlural: "marketing updates", icon: Globe, classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" }, order: 10 },
  { key: "incentive", group: "task", label: "Incentive", caption: "Results from incentive calculations and runs", clauseSingular: "incentive update", clausePlural: "incentive updates", icon: Trophy, classes: { bg: "bg-ochre", text: "text-ochre-foreground", ring: "ring-ochre/20" }, order: 20 },
  { key: "import", group: "task", label: "Import & export", caption: "Imports and exports of board data", clauseSingular: "import or export update", clausePlural: "import and export updates", icon: ArrowUpDown, classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" }, order: 30 },
  { key: "context", group: "task", label: "Context", caption: "Board context builds, questions, and suggestions", clauseSingular: "context update", clausePlural: "context updates", icon: Layers, classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" }, order: 40 },
  { key: "copy", group: "task", label: "Copy", caption: "Board and entity copy jobs", clauseSingular: "copy update", clausePlural: "copy updates", icon: Copy, classes: { bg: "bg-clay", text: "text-clay-foreground", ring: "ring-clay/20" }, order: 50 },
  { key: "reminders", group: "alert", label: "Reminders", caption: "Entity reminders that need a look", clauseSingular: "reminder", clausePlural: "reminders", icon: Bell, classes: { bg: "bg-warning", text: "text-warning-foreground", ring: "ring-warning/20" }, order: 70 },
  { key: "schedule", group: "alert", label: "Schedule", caption: "Changes to task and resource schedules", clauseSingular: "schedule notification", clausePlural: "schedule notifications", icon: CalendarClock, classes: { bg: "bg-rose", text: "text-rose-foreground", ring: "ring-rose/20" }, order: 80 },
  { key: "nudges", group: "alert", label: "Nudges", caption: "Nudges for checklists you're on", clauseSingular: "nudge", clausePlural: "nudges", icon: ListChecks, classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" }, order: 90 },
  { key: "board_alert", group: "alert", label: "Board alert", caption: "Board-wide alerts for you", clauseSingular: "board alert", clausePlural: "board alerts", icon: ShieldAlert, classes: { bg: "bg-destructive", text: "text-destructive-foreground", ring: "ring-destructive/20" }, order: 100 },
  { key: "status", group: "alert", label: "Status", caption: "Entity status changes across the board", clauseSingular: "Status change", clausePlural: "Status changes", icon: ArrowLeftRight, classes: { bg: "bg-slate", text: "text-slate-foreground", ring: "ring-slate/20" }, order: 110 },
  { key: "general", group: "alert", label: "General", caption: "General notifications for you", clauseSingular: "general update", clausePlural: "general updates", icon: Megaphone, classes: { bg: "bg-chart-3", text: "text-white", ring: "ring-chart-3/20" }, order: 120 },
  { key: "notes", group: "alert", label: "Notes", caption: "Notes added on entities you follow", clauseSingular: "note", clausePlural: "notes", icon: StickyNote, classes: { bg: "bg-ochre", text: "text-ochre-foreground", ring: "ring-ochre/20" }, order: 125 },
  { key: "file", group: "alert", label: "Files", caption: "File activity on entities you follow", clauseSingular: "file update", clausePlural: "file updates", icon: FileText, classes: { bg: "bg-stone-500", text: "text-white", ring: "ring-stone-500/20" }, order: 130 },
  { key: "leads", group: "alert", label: "Leads", caption: "New and updated leads for you", clauseSingular: "Lead", clausePlural: "Leads", icon: Target, classes: { bg: "bg-success", text: "text-success-foreground", ring: "ring-success/20" }, order: 140 },
];

export function getNotificationCategory(key: string): CategoryMeta | undefined {
  return CATEGORIES.find((c) => c.key === key);
}

/**
 * Card eyebrow for the shared `import` API category: Import vs Export by async type.
 * Section title stays "Import & export"; individual cards name the operation.
 */
export function getImportExportOperationEyebrow(
  entry: Pick<NotificationTypeEntry, "categoryKey" | "type">,
): string | null {
  if (entry.categoryKey !== "import") return null;
  if (
    entry.type === ASYNC_TASK_TYPES.CSV_EXPORT ||
    entry.type === ASYNC_TASK_TYPES.BOARD_EXPORT
  ) {
    return "Export";
  }
  return "Import";
}

export function toApiParams(
  filter: NotificationFilter,
): Pick<
  NotificationListParams,
  "category" | "notable" | "async_status" | "notification_status" | "is_seen"
> {
  switch (filter.kind) {
    case "none":
      return {};
    case "notable":
      return { notable: true };
    case "category":
      return {
        category: filter.category,
        ...(filter.excludeNotable ? { notable: false } : {}),
      };
  }
}

/**
 * Mark-all-seen POST body — surface selector only (server applies unseen-only when
 * `is_seen` is omitted). Notable → `{ notable: true }`; category detail →
 * `{ category }` so every item in that category is cleared.
 */
export function toMarkAllSeenBody(
  filter: NotificationFilter,
): Pick<NotificationListParams, "notable" | "category"> | null {
  if (filter.kind === "notable") return { notable: true };
  if (filter.kind === "category") return { category: filter.category };
  return null;
}

// ─── Count Mapping ─────────────────────────────────────────────────────────────

export function mapNotificationCounts(
  countResponse: NotificationCountsResponse,
): MappedNotificationCounts {
  const byCategory: Record<string, number> = {};

  for (const raw of countResponse.category_counts ?? []) {
    if (!raw.category || raw.count <= 0) continue;
    byCategory[raw.category] = (byCategory[raw.category] ?? 0) + raw.count;
  }

  const categories: NotificationMappedCategoryCount[] = Object.entries(byCategory)
    .map(([key, count]) => {
      const meta = getNotificationCategory(key);
      const label = meta?.label
        ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        value: key,
        label,
        icon: meta?.icon ?? AlertTriangle,
        group: meta?.group ?? ("alert" as const),
        classes: meta?.classes ?? {
          bg: "bg-muted",
          text: "text-muted-foreground",
          ring: "ring-muted/20",
        },
        filters: NOTIFICATION_CONFIG.filter((e) => e.categoryKey === key).map((e) => e.filter),
        count,
        filter: { kind: "category" as const, category: key },
        _order: meta?.order ?? 999,
      };
    })
    .sort((a, b) => a._order - b._order)
    .map(({ _order: _, ...rest }) => rest);

  return {
    total: countResponse.total_count,
    unread: countResponse.unread_count ?? 0,
    notable: countResponse.notable_count ?? 0,
    categories,
    byCategory,
  };
}
